import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import { useSpeechInput } from '../../hooks/useSpeechInput';
import axiosClient from '../../api/axios.client.js';
import { sendFormBuilderAiChat, sendFormBuilderAiChatV2 } from '../../api/formBuilder.api.js';
import { STUDIO_V2_DEFAULT_AI_MODEL } from '../studio/studioV2DefaultAiModel.js';

function summarizeSnapshot(snapshot) {
  const fe = snapshot?.formElements;
  const n = Array.isArray(fe) ? fe.length : 0;
  return { elementCount: n, types: Array.isArray(fe) ? [...new Set(fe.map((e) => e?.type).filter(Boolean))] : [] };
}

/**
 * AI assistant for Form Builder — POST /api/v1/forms/design/ai-chat with live editor snapshot.
 */
export default function FormBuilderAiChatPanel({
  snapshot,
  formDesignId,
  onApplySnapshot,
  /** When true (e.g. Studio embedded form designer), call POST /api/v2/forms/design/ai-chat. */
  useV2AiEndpoint = false,
}) {
  const [models, setModels] = useState([]);
  const [model, setModel] = useState(STUDIO_V2_DEFAULT_AI_MODEL);
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      role: 'assistant',
      content: 'Describe the form you want (fields, validation, layout). When the model returns a JSON block with `formElements`, you can apply it to the canvas.',
    },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [lastProposal, setLastProposal] = useState(null);
  const [attachDiagnostics, setAttachDiagnostics] = useState(false);
  const bottomRef = useRef(null);

  const handleSpeechTranscript = useCallback((text) => {
    if (!text) return;
    setInput((prev) => prev + text);
  }, []);
  const { isListening, startListening, stopListening, hasSupport } = useSpeechInput(handleSpeechTranscript);

  const buildClientDiagnostics = useCallback(() => {
    const s = summarizeSnapshot(snapshot);
    return {
      source: 'form-builder-ai-panel',
      ...s,
    };
  }, [snapshot]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await axiosClient.get('models');
        const list = data?.models || [];
        if (cancelled) return;
        setModels(list);
        const names = list.map((m) => m.name);
        if (names.includes(STUDIO_V2_DEFAULT_AI_MODEL)) setModel(STUDIO_V2_DEFAULT_AI_MODEL);
        else if (names.length) setModel(names[0]);
      } catch {
        /* optional */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, busy]);

  const onSend = useCallback(async () => {
    const t = input.trim();
    if (!t || busy) return;
    setErr(null);
    setLastProposal(null);
    const userMsg = { id: `u_${Date.now()}`, role: 'user', content: t };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setBusy(true);

    const historyForApi = [
      ...messages.filter((m) => m.id !== 'welcome' && (m.role === 'user' || m.role === 'assistant')),
      userMsg,
    ];
    const apiMessages = historyForApi.map((m) => ({ role: m.role, content: m.content }));

    const clientDiagnostics = attachDiagnostics ? buildClientDiagnostics() : undefined;
    const send = useV2AiEndpoint ? sendFormBuilderAiChatV2 : sendFormBuilderAiChat;
    const {
      reply, error, proposedSnapshot, proposedValidation,
    } = await send({
      messages: apiMessages,
      model,
      snapshot,
      formDesignId,
      clientDiagnostics,
    });

    setBusy(false);
    if (error) {
      setErr(error);
      return;
    }
    const asst = { id: `a_${Date.now()}`, role: 'assistant', content: reply || '(empty response)' };
    setMessages((prev) => [...prev, asst]);
    if (proposedSnapshot) setLastProposal(proposedSnapshot);
    else if (proposedValidation && !proposedValidation.valid) {
      setErr(`Proposal failed validation: ${(proposedValidation.errors || []).join('; ') || 'unknown'}`);
    }
  }, [input, busy, messages, model, snapshot, formDesignId, attachDiagnostics, buildClientDiagnostics, useV2AiEndpoint]);

  const onApply = useCallback(() => {
    if (!lastProposal) return;
    onApplySnapshot?.(lastProposal);
    setLastProposal(null);
    setMessages((prev) => [...prev, {
      id: `sys_${Date.now()}`,
      role: 'assistant',
      content: 'Applied the proposed form to the editor. Review Design and Preview, then Save if you want it persisted.',
    }]);
  }, [lastProposal, onApplySnapshot]);

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, maxWidth: 960, mx: 'auto' }}>
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        AI form assistant
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        Uses your tenant LLM settings. The server receives the current fields and form settings from this tab’s snapshot (not only the last saved version).
        {useV2AiEndpoint ? ' Requests use POST /api/v2/forms/design/ai-chat (same contract as v1).' : ''}
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 1.5 }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="form-ai-model">Model</InputLabel>
          <Select
            labelId="form-ai-model"
            label="Model"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            disabled={busy}
          >
            {(models.length ? models : [{ name: STUDIO_V2_DEFAULT_AI_MODEL, displayName: 'Qwen 2.5 Coder 14B (Ollama)' }]).map((m) => (
              <MenuItem key={m.name} value={m.name}>{m.displayName || m.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={attachDiagnostics}
              onChange={(e) => setAttachDiagnostics(e.target.checked)}
              disabled={busy}
            />
          )}
          label={(
            <Typography variant="body2" component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <BugReportOutlinedIcon sx={{ fontSize: 18, opacity: 0.85 }} />
              Attach field summary (counts / types)
            </Typography>
          )}
        />
      </Stack>

      {err && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setErr(null)}>{err}</Alert>}

      <Box sx={{
        maxHeight: 380,
        overflow: 'auto',
        p: 1,
        mb: 1,
        borderRadius: 1,
        bgcolor: 'action.hover',
        border: 1,
        borderColor: 'divider',
      }}
      >
        {messages.map((m) => (
          <Box
            key={m.id}
            sx={{
              mb: 1,
              display: 'flex',
              justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
            }}
          >
            <Paper
              elevation={0}
              sx={{
                px: 1.25,
                py: 0.75,
                maxWidth: '88%',
                bgcolor: m.role === 'user' ? 'primary.dark' : 'background.paper',
                color: m.role === 'user' ? 'primary.contrastText' : 'text.primary',
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', mb: 0.25 }}>
                {m.role}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 13 }}>
                {m.content}
              </Typography>
            </Paper>
          </Box>
        ))}
        {busy && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        <div ref={bottomRef} />
      </Box>

      <Stack direction="row" spacing={1} alignItems="flex-end">
        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={6}
          size="small"
          placeholder="Type or tap mic — e.g. add email, phone, and a required textarea…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={busy}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              onSend();
            }
          }}
        />
        {hasSupport && (
          <Tooltip title={isListening ? 'Stop listening' : 'Voice input'}>
            <span>
              <IconButton
                onClick={isListening ? stopListening : startListening}
                disabled={busy}
                color={isListening ? 'error' : 'default'}
                aria-label={isListening ? 'Stop voice input' : 'Voice input'}
              >
                {isListening ? <MicOffIcon /> : <MicIcon />}
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Button variant="contained" endIcon={<SendIcon />} onClick={onSend} disabled={busy || !input.trim()}>
          Send
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
        Ctrl+Enter to send · Snapshot:
        {' '}
        {Array.isArray(snapshot?.formElements) ? snapshot.formElements.length : 0}
        {' '}
        fields
      </Typography>

      {lastProposal && (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap">
          <Button variant="contained" color="success" onClick={onApply}>
            Apply proposed form
          </Button>
          <Button size="small" onClick={() => setLastProposal(null)}>Dismiss proposal</Button>
        </Stack>
      )}
    </Paper>
  );
}
